import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import { colors } from '../styles/colors';

interface MenuState {
  uid: string;
  nickname: string;
  frequency?: string;
  distance?: string;
  hideJump?: boolean;
  x: number;
  y: number;
}

export default function UserContextMenu() {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const user = useAuthStore((s) => s.user);
  const [menu, setMenu] = useState<MenuState | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const d = (e as CustomEvent).detail;
      setMenu({ uid: d.uid, nickname: d.nickname, frequency: d.frequency, distance: d.distance, hideJump: d.hideJump, x: d.x ?? window.innerWidth / 2, y: d.y ?? window.innerHeight / 2 });
    };
    window.addEventListener('open-user-menu', handler);
    return () => window.removeEventListener('open-user-menu', handler);
  }, []);

  if (!menu || !user) return null;

  const ic = (ch: string, color: string) => (
    <span className="flex h-5 w-5 items-center justify-center rounded text-[10px] font-bold"
      style={{ backgroundColor: color + '20', color }}>{ch}</span>
  );

  const items = [
    { label: isKo ? '프로필' : 'Profile', icon: ic('P', colors.primary), action: 'profile' },
    { label: isKo ? '쪽지 보내기' : 'Send Message', icon: ic('M', colors.info), action: 'dm' },
    ...(menu.frequency && !menu.hideJump ? [{ label: isKo ? '위치 점프' : 'Location Jump', icon: ic('J', '#FFB300'), action: 'jump', color: '#FFB300' }] : []),
    ...(menu.frequency ? [{ label: isKo ? '주파수 복사' : 'Copy Frequency', icon: ic('F', '#8bb8e8'), action: 'copyFreq' }] : []),
    { label: isKo ? '신고' : 'Report', icon: ic('!', colors.warning), action: 'report' },
    { label: isKo ? '차단' : 'Block', icon: ic('X', colors.error), action: 'block', color: colors.error },
  ];

  const close = () => setMenu(null);

  return (
    <>
      <div className="fixed inset-0 z-50" onClick={close} />
      <div className="fixed z-50 min-w-[180px] overflow-hidden rounded-lg shadow-2xl"
        ref={(el) => {
          if (!el) return;
          const rect = el.getBoundingClientRect();
          const vw = window.innerWidth;
          const vh = window.visualViewport?.height ?? window.innerHeight;
          let x = menu.x; let y = menu.y;
          if (x + rect.width > vw - 8) x = vw - rect.width - 8;
          if (x < 8) x = 8;
          if (y + rect.height > vh - 8) y = y - rect.height - 8;
          if (y < 8) y = 8;
          el.style.left = `${x}px`; el.style.top = `${y}px`;
        }}
        style={{ left: -9999, top: -9999, backgroundColor: colors.card, border: `1.5px solid ${colors.border}` }}>
        <div className="border-b px-3 py-2" style={{ borderColor: colors.border }}>
          <p className="text-xs font-bold" style={{ color: colors.text }}>{menu.nickname}</p>
          {menu.frequency && <p className="font-lcd text-[0.5625rem] tracking-wider" style={{ color: colors.lcdNeon }}>{menu.frequency}</p>}
          {menu.distance && <p className="text-[0.625rem]" style={{ color: colors.textMuted }}>{menu.distance}</p>}
        </div>
        {items.map((item) => (
          <button key={item.action}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-white/5"
            style={{ color: (item as any).color ?? colors.text }}
            onClick={() => {
              close();
              if (item.action === 'profile') window.dispatchEvent(new CustomEvent('open-profile', { detail: { uid: menu.uid, nickname: menu.nickname } }));
              else if (item.action === 'dm') window.dispatchEvent(new CustomEvent('open-dm-compose', { detail: { recipientId: menu.uid, recipientNickname: menu.nickname } }));
              else if (item.action === 'jump' && menu.frequency) {
                import('../pages/radio/RadioPage').catch(() => {});
                // Jump via location store
                const freq = menu.frequency;
                import('../stores/locationStore').then(({ useLocationStore }) => {
                  const jumpRange = user?.locationJumpRange ?? { min: 100, max: 150 };
                  useLocationStore.getState().teleport(freq, jumpRange);
                });
              }
              else if (item.action === 'copyFreq' && menu.frequency) navigator.clipboard.writeText(menu.frequency);
              else if (item.action === 'report') window.dispatchEvent(new CustomEvent('open-report', { detail: { targetId: menu.uid, targetNickname: menu.nickname, contentType: 'nearby' } }));
              else if (item.action === 'block') {
                if (confirm(`${menu.nickname}${isKo ? '님을 차단하시겠습니까?' : ' - Block this user?'}`)) {
                  import('../services/userService').then(({ blockUser }) => {
                    blockUser(user.uid, menu.uid);
                    useAuthStore.getState().setUser({ ...user, blockedUserIds: [...(user.blockedUserIds ?? []), menu.uid] });
                  });
                }
              }
            }}>
            {item.icon}{item.label}
          </button>
        ))}
      </div>
    </>
  );
}
